import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Save, Shield, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const MODES = [
  { key: "allowed", label: "Allowed", color: "emerald" },
  { key: "denied", label: "Denied", color: "slate" },
];

export default function CollaboratorPermissions() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [user, setUser] = useState(null);
  const [perms, setPerms] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [cat, u] = await Promise.all([
          api.get("/collaborators/catalog"),
          api.get(`/collaborators/${id}`),
        ]);
        if (!mounted) return;
        setCatalog(cat.data);
        setUser(u.data);
        setPerms(u.data.permissions || {});
      } catch (e) {
        toast.error(e.response?.data?.detail || t("collab.load_failed", "Could not load"));
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [id, t]);

  const sensitive = useMemo(() => new Set(catalog?.sensitive || []), [catalog]);

  const setMode = (key, mode) => {
    setPerms((p) => ({ ...p, [key]: mode }));
    setDirty(true);
  };

  const applyTemplate = (tplKey) => {
    const tpl = (catalog?.templates || []).find((x) => x.key === tplKey);
    if (!tpl) return;
    // Coerce any legacy "approval" values from the template to "allowed"
    // — the middle-state was removed in iter59.
    const coerced = Object.fromEntries(
      Object.entries(tpl.permissions || {}).map(([k, v]) => [k, v === "approval" ? "allowed" : v]),
    );
    setPerms(coerced);
    setDirty(true);
    toast.info(t("collab.template_applied", "Template applied — remember to save"));
  };

  const setGroup = (groupKey, mode) => {
    const grp = (catalog?.groups || []).find((g) => g.key === groupKey);
    if (!grp) return;
    setPerms((prev) => {
      const next = { ...prev };
      grp.permissions.forEach((p) => {
        next[p.key] = mode;
      });
      return next;
    });
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/collaborators/${id}/permissions`, { permissions: perms });
      toast.success(t("collab.perms_saved", "Permissions saved"));
      setDirty(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  if (loading) return <div className="text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> {t("common.loading")}</div>;
  if (!catalog || !user) return null;

  return (
    <div className="space-y-6" data-testid="collab-perms-page">
      <button onClick={() => navigate("/collaborators")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /> {t("common.back", "Back")}</button>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Permissions</div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {user.name} <span className="text-slate-500 text-lg font-medium">({user.username})</span>
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">{user.job_title || "—"} · <span className={user.active ? "text-emerald-600" : "text-slate-500"}>{user.active ? "Active" : "Suspended"}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="collab-perms-template" onChange={(e) => e.target.value && applyTemplate(e.target.value)} value="" className="h-10 rounded-lg border border-slate-300 bg-white px-2 text-sm">
            <option value="">{t("collab.apply_template", "Apply template…")}</option>
            {catalog.templates.map((tp) => (<option key={tp.key} value={tp.key}>{tp.label}</option>))}
          </select>
          <button data-testid="collab-perms-save" onClick={save} disabled={!dirty || busy} className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t("common.save")}
          </button>
        </div>
      </div>

      <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-xs px-3 py-2 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>{t("collab.perms_zero_trust", "All permissions default to Denied. Sensitive permissions (highlighted) should only be granted deliberately. The Master Admin is the sole decider — toggle each row to Allowed or Denied.")}</div>
      </div>

      <div className="space-y-4">
        {catalog.groups.map((group) => (
          <div key={group.key} className="bg-white border border-slate-200 rounded-md" data-testid={`collab-group-${group.key}`}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#0F62FE]">{group.label}</div>
                <div className="text-sm text-slate-500">{group.permissions.length} {t("collab.permissions", "permissions")}</div>
              </div>
              <div className="flex items-center gap-1">
                {MODES.map((m) => (
                  <button key={m.key} data-testid={`collab-group-${group.key}-set-${m.key}`} onClick={() => setGroup(group.key, m.key)} className="h-8 px-2 rounded-md border border-slate-300 bg-white text-xs text-slate-700 hover:bg-slate-50">
                    All {m.label.toLowerCase()}
                  </button>
                ))}
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {group.permissions.map((p) => {
                const current = perms[p.key] || "denied";
                const isSensitive = sensitive.has(p.key);
                return (
                  <div key={p.key} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`perm-row-${p.key}`}>
                    <div className="min-w-0 flex-1 flex items-center gap-2">
                      {isSensitive && <Shield className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" title="Sensitive" />}
                      <div className="text-sm text-slate-900 truncate">{p.label}</div>
                      {isSensitive && <span className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Sensitive</span>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {MODES.map((m) => {
                        const on = current === m.key;
                        return (
                          <button key={m.key} data-testid={`perm-${p.key}-${m.key}`} onClick={() => setMode(p.key, m.key)}
                            className={
                              "h-8 px-3 rounded-md text-xs font-semibold border transition-colors " +
                              (on
                                ? m.color === "emerald" ? "bg-emerald-500 text-white border-emerald-500"
                                  : "bg-slate-700 text-white border-slate-700"
                                : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50")
                            }>
                            {m.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
